import { ReactElement } from 'react';
import { ALL_SOURCES } from '@fabric-ui/components';
import { DraggableProvided } from '@hello-pangea/dnd';

export interface IUiConfigSettingsProps {
  projectionName: string;
}

export type TField = {
  field: string;
  displayName?: string;
  isJson?: boolean;
  isHTML?: boolean;
  hrefField?: string;
  isAIField?: boolean;
  isEntityField?: boolean;
  defaultExpanded?: boolean;
  ingressSources?: (string | typeof ALL_SOURCES)[];
  showNull?: boolean;
};

export type TExpandedRowDefObject = {
  type?: string;
  fields: TField[];
};

type TExpandedRowDef = TExpandedRowDefObject | string | TField[];

export enum TabTypeEnum {
  MAIN = 'main',
  VULNERABILITY = 'vulnerability',
  FINDINGS = 'findings',
  ALERTS = 'alerts',
  ASSETS = 'assets',
  FIX = 'fix',
  SCORE_EXPLANATION = 'scoreExplanation',
  INGRESS_ASSETS = 'ingressAssets',
  CUSTOM = 'custom',
  SCHEDULED_QUERY = 'scheduledQuery',
  EVENTS = 'events',
  POLICY_VIOLATIONS = 'policyViolations',
  ENTITIES = 'entities',
  ALL_ALERT_DETAILS = 'allAlertDetails',
  INCIDENTS = 'incidents',
  ATTACK_PATH = 'attackPath',
  MITRE_ATTACK = 'MitreAttack',
  REMEDIATION = 'Remediation',
  IDENTITY_FINDINGS = 'identityFindings',
  USER_APPLICATIONS = 'userApplications',
  USER_ASSETS = 'userAssets',
  USER_ENTRA_APPLICATIONS = 'userEntraApplications',
  USER_ROLES = 'userRoles',
  USER_APP_ROLES = 'userAppRoles',
  USER_GROUPS = 'userGroups',
  USER_TENANTS = 'userTenants',
  IDENTITY_FINDINGS_AFFECTED_ENTITIES = 'identityFindingsEffectedEntities',
  USERS = 'users',
  FINDING_EVIDENCE = 'findingEvidence',
  ALL_IDENTITY_FINDINGS_DETAILS = 'allIdentityFindingsDetails',
}

export type TTabTypeEnum = (typeof TabTypeEnum)[keyof typeof TabTypeEnum];
export type TTab = {
  name: string;
  dataType: string;
  tabType: TTabTypeEnum;
  fields: TField[] | TField[][];
  expandedRowDef?: TExpandedRowDef;
  shouldShowTabField?: string;
  projection?: string;
};

export type TOnEditOpen = {
  tabIndex: number;
  field?: TField;
  isExpandedField?: boolean;
  fields?: TField[] | TField[][];
};

export type TEditItem = Partial<{
  tabIndex: number;
  field: TField;
  target: HTMLElement | null;
  isExpandedField: boolean;
  fields: TField[] | TField[][];
}>;

type TCrudField = { tabIndex: number; field: TField; fields: TField[] | TField[][]; columnPosition?: number; isExpanded?: boolean };

export type TAddAndDeleteField = Omit<TCrudField, 'fields'>;

export type TOnChangeFields = Omit<TCrudField, 'field'>;

export type TOnEditField = TAddAndDeleteField & { currentFieldName: string };

export type TOnEditTab = Partial<TTab> & { isExpandedRow?: boolean };

type TOnEditOpenEventCb = ({ currentTarget }: { currentTarget: HTMLButtonElement }) => void;

export interface ITabHeaderProps {
  name: string;
  dataType: string;
  tabType: TTabTypeEnum;
  tabIndex: number;
  onEditOpen: (args: TOnEditOpen) => TOnEditOpenEventCb;
  deleteTab: (tabIndex: number) => void;
  onChangeInnerTab: (tabIndex: number, innerTabIndex: string) => void;
  getInnerTabValue: (tabIndex: number) => string;
  providedDragHandleProps: DraggableProvided['dragHandleProps'];
}

export interface ITabContentColumnProps {
  tabData: TTab & { columnPosition?: number; tabIndex: number };
  onEditOpen: (args: TOnEditOpen) => TOnEditOpenEventCb;
  addField: (args: TAddAndDeleteField) => void;
  deleteField: (args: TAddAndDeleteField) => void;
  fieldOptions: any[];
  disabled?: boolean;
  onChangeFields: (args: TOnChangeFields) => void;
  getInnerTabValue?: (tabIndex: number) => string;
  expandedRowDef?: TExpandedRowDef;
}

export interface IAddTabFieldPopperProps {
  tabIndex?: number;
  tabType: string;
  open: boolean;
  anchorEl?: HTMLElement | null;
  handleClose: () => void;
  value?: { field: TField; tab: TTab };
  fieldOptions: any[];
  onApply: (args: any) => void;
  disabledOptions: string[];
}

export interface IFieldTitleProps {
  tabName: string;
  title: ReactElement<any> | null;
  field: string;
  dragProvided: DraggableProvided;
  disabled: boolean;
  onEditOpen: TOnEditOpenEventCb;
  deleteField: () => void;
}
export enum InnerTabsEnum {
  SECTIONS = 'sections',
  FIELDS = 'fields',
}

export const COLUMN_WIDTH = 200;
export const DISABLED_TAB_TYPES_FOR_EDITING: TabTypeEnum[] = [
  TabTypeEnum.FIX,
  TabTypeEnum.SCORE_EXPLANATION,
  TabTypeEnum.MITRE_ATTACK,
  TabTypeEnum.REMEDIATION,
  TabTypeEnum.ALL_IDENTITY_FINDINGS_DETAILS,
];
